/**
 * Thin wrapper around the workshop admin endpoints.
 *
 * All admin endpoints live under `/workshop/admin/*` and require the
 * authenticated user to be an admin (enforced server-side).
 */
import { open_api_with_cookies } from "./network";

const ADMIN_BASE = "/workshop/admin";

// Pagination/result unwrappers. Different DRF endpoints in this backend return
// either `{data: ...}` (custom renderer) or the standard
// `{count, next, previous, results: []}` paginator. This helper hides that.
const unwrap = (res) => {
  const d = res?.data;
  if (d && Object.prototype.hasOwnProperty.call(d, "data")) return d.data;
  return d;
};

const unwrapList = (res) => {
  const raw = unwrap(res);
  if (!raw) return { items: [], count: 0, next: null, previous: null };
  if (Array.isArray(raw))
    return { items: raw, count: raw.length, next: null, previous: null };
  if (Array.isArray(raw.results)) {
    return {
      items: raw.results,
      count: raw.count ?? raw.results.length,
      next: raw.next ?? null,
      previous: raw.previous ?? null,
    };
  }
  return { items: [], count: 0, next: null, previous: null };
};

// ---------- Workshops CRUD --------------------------------------------------

export const listWorkshops = async (params = {}) => {
  const res = await open_api_with_cookies.get(`${ADMIN_BASE}/workshops/`, {
    params,
  });
  return unwrapList(res);
};

export const getWorkshop = async (id) => {
  const res = await open_api_with_cookies.get(`${ADMIN_BASE}/workshops/${id}/`);
  return unwrap(res);
};

export const createWorkshop = async (payload) => {
  const res = await open_api_with_cookies.post(
    `${ADMIN_BASE}/workshops/`,
    payload
  );
  return unwrap(res);
};

export const updateWorkshop = async (id, payload) => {
  const res = await open_api_with_cookies.patch(
    `${ADMIN_BASE}/workshops/${id}/`,
    payload
  );
  return unwrap(res);
};

export const deleteWorkshop = async (id) => {
  await open_api_with_cookies.delete(`${ADMIN_BASE}/workshops/${id}/`);
  return true;
};

export const toggleWorkshopActive = async (id) => {
  const res = await open_api_with_cookies.post(
    `${ADMIN_BASE}/workshops/${id}/toggle-active/`
  );
  return unwrap(res);
};

export const duplicateWorkshop = async (id) => {
  const res = await open_api_with_cookies.post(
    `${ADMIN_BASE}/workshops/${id}/duplicate/`
  );
  return unwrap(res);
};

export const uploadWorkshopBanner = async (id, file) => {
  const formData = new FormData();
  formData.append("file", file);
  const res = await open_api_with_cookies.post(
    `${ADMIN_BASE}/workshops/${id}/upload-banner/`,
    formData,
    { headers: { "Content-Type": "multipart/form-data" } }
  );
  return unwrap(res);
};

// ---------- Dropdowns -------------------------------------------------------

export const listWorkshopTypes = async () => {
  const res = await open_api_with_cookies.get(`${ADMIN_BASE}/workshop-types/`);
  return unwrapList(res).items;
};

export const listCategories = async (params = {}) => {
  const res = await open_api_with_cookies.get(`${ADMIN_BASE}/categories/`, {
    params,
  });
  return unwrapList(res).items;
};

export const listAudiences = async () => {
  const res = await open_api_with_cookies.get(`${ADMIN_BASE}/audiences/`);
  return unwrapList(res).items;
};

export const listTags = async () => {
  const res = await open_api_with_cookies.get(`${ADMIN_BASE}/tags/`);
  return unwrapList(res).items;
};

export const listWhatYouLearn = async () => {
  const res = await open_api_with_cookies.get(
    `${ADMIN_BASE}/what-you-learn/`
  );
  return unwrapList(res).items;
};

export const listWhyWorkshop = async () => {
  const res = await open_api_with_cookies.get(`${ADMIN_BASE}/why-workshop/`);
  return unwrapList(res).items;
};

export const listExperts = async (params = {}) => {
  const res = await open_api_with_cookies.get(`${ADMIN_BASE}/experts/`, {
    params,
  });
  return unwrapList(res).items;
};

// ---------- Constants -------------------------------------------------------

export const WORKSHOP_TYPE_OPTIONS = [
  { value: "1", label: "Self Care" },
  { value: "2", label: "Child Care" },
  { value: "3", label: "Pregnancy Care" },
];

export const VIDEO_TYPEOF_OPTIONS = [
  { value: "1", label: "Reels" },
  { value: "2", label: "Lecture" },
];

export const MEDIA_CATEGORY_OPTIONS = [
  { value: 1, label: "Video" },
  { value: 2, label: "App / Top" },
  { value: 3, label: "Bottom" },
];

// Helper for collapsing list-API responses that may or may not wrap in {data: []}
export const asArray = (raw) => {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw;
  if (Array.isArray(raw.data)) return raw.data;
  if (Array.isArray(raw.results)) return raw.results;
  return [];
};
